import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import api from "../lib/api";

const WHATSAPP_HREF = "https://wa.me/393520463627";

/**
 * Lista pubblica di veicoli (auto o moto).
 * Foto ferma, testo che sale in fretta. Ogni card: nome, prezzo,
 * WhatsApp, Scheda.
 */
export default function VehicleList({ type = "auto" }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get("/drops")
      .then((r) => {
        const rows = (Array.isArray(r.data) ? r.data : []).filter(
          (d) => (d.vehicle_type || "auto") === type && d.is_published !== false
        );
        setItems(rows);
      })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [type]);

  const featured = items.find((d) => d.is_featured);
  const rest = featured ? items.filter((d) => d.id !== featured.id) : items;

  const title = type === "moto" ? "Moto." : "Auto.";
  const subtitle =
    type === "moto"
      ? "In vendita a Brescia. Ogni moto ha una storia, un prezzo chiaro, e si vede in persona."
      : "In vendita a Brescia. Ogni auto ha una storia, un prezzo chiaro, e si vede in persona.";

  return (
    <div style={{ background: "#F2EFE9", minHeight: "100vh", paddingTop: 64 }} data-testid={`vehicle-list-${type}`}>
      {/* Intro */}
      <motion.section
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="px-6 sm:px-12 pt-20 pb-14"
      >
        <div className="max-w-[1200px] mx-auto">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
            <div className="text-[11px] uppercase tracking-[0.3em]" style={{ color: "#FF2800" }}>
              Marketplace · {type === "moto" ? "Moto in collezione" : "Auto in collezione"}
            </div>
            <TypeSwitch type={type} />
          </div>
          <h1 className="text-5xl sm:text-7xl mb-6" style={{ color: "#111", fontWeight: 300, letterSpacing: "-0.02em" }}>
            {title}
          </h1>
          <p className="text-[17px] max-w-[620px]" style={{ color: "#5a5a5a", lineHeight: 1.6 }}>
            {subtitle}
          </p>
        </div>
      </motion.section>

      {/* Veicolo del mese */}
      {featured ? <FeaturedHero v={featured} type={type} /> : null}

      {/* Le altre auto/moto in pannelli full-viewport 3D (come home) */}
      <section className="pb-32" data-testid="vehicle-others">
        <div className="max-w-[1320px] mx-auto px-6 sm:px-12 pt-6">
          <div className="flex items-baseline justify-between mb-4 flex-wrap gap-3">
            <h2
              className="text-3xl sm:text-4xl"
              style={{ color: "#111", fontWeight: 300, letterSpacing: "-0.02em" }}
            >
              {featured
                ? type === "moto" ? "Le altre moto." : "Le altre auto."
                : type === "moto" ? "Tutte le moto." : "Tutte le auto."}
            </h2>
            <div className="text-[11px] uppercase tracking-[0.3em]" style={{ color: "#8a8a8a" }}>
              {rest.length} {type === "moto" ? "moto" : "auto"}
            </div>
          </div>
        </div>
        {loading ? (
          <div className="px-6 sm:px-12 text-[13px] uppercase tracking-widest" style={{ color: "#8a8a8a" }}>
            Caricamento…
          </div>
        ) : rest.length > 0 ? (
          rest.map((v, i) => (
            <VehiclePanel key={v.id || v.slug} v={v} index={i} type={type} />
          ))
        ) : featured ? (
          <div className="max-w-[1320px] mx-auto px-6 sm:px-12">
            <OtherEmpty type={type} />
          </div>
        ) : (
          <div className="max-w-[1320px] mx-auto px-6 sm:px-12">
            <EmptyState type={type} />
          </div>
        )}
      </section>
    </div>
  );
}

/**
 * Pannello full-viewport per una singola auto/moto — stessa estetica dei
 * pannelli della home: foto full-bleed con parallax + tilt 3D, testo
 * grande in overlay. NON include il video YouTube (quello sta solo sul
 * Veicolo del mese in cima e nella scheda dettaglio per non creare
 * confusione).
 */
function VehiclePanel({ v, index, type }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  // Effetto 3D "camera in / camera out" come la home
  const imgY = useTransform(scrollYProgress, [0, 1], ["-14%", "14%"]);
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [1.08, 1, 1.08]);
  const rotateX = useTransform(scrollYProgress, [0, 0.5, 1], [12, 0, -12]);
  const opacity = useTransform(scrollYProgress, [0, 0.18, 0.82, 1], [0, 1, 1, 0]);
  const contentY = useTransform(scrollYProgress, [0, 0.5, 1], [80, 0, -80]);
  const contentScale = useTransform(scrollYProgress, [0, 0.5, 1], [0.92, 1, 0.92]);

  const hero = v.hero_image || (v.gallery && v.gallery[0]) || "";
  const heroSrc = hero && hero.startsWith("/")
    ? `${process.env.REACT_APP_BACKEND_URL}${hero}`
    : hero;
  const price = v.price ? formatPrice(v.price) : "";
  const waHref = `${WHATSAPP_HREF}?text=${encodeURIComponent(
    `Ciao, mi interessa ${v.title} (${v.year || ""})`
  )}`;

  return (
    <section
      ref={ref}
      className="relative w-full overflow-hidden"
      style={{ height: "100vh", minHeight: 560, perspective: 1200, background: "#111" }}
      data-testid={`vehicle-panel-${v.slug}`}
    >
      {/* Background */}
      <motion.div className="absolute inset-0" style={{ y: imgY, scale }}>
        {heroSrc ? (
          <img
            src={heroSrc}
            alt={v.title || ""}
            loading="lazy"
            decoding="async"
            className="w-full h-full object-cover"
          />
        ) : (
          <div
            className="absolute inset-0 flex items-center justify-center"
            style={{ background: "linear-gradient(135deg, #d8d3c9 0%, #b5aea3 100%)" }}
          >
            <span className="text-[10px] uppercase tracking-[0.4em]" style={{ color: "rgba(0,0,0,0.35)" }}>
              Foto in arrivo
            </span>
          </div>
        )}
      </motion.div>

      {/* Overlay for legibility */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "linear-gradient(180deg, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0) 30%, rgba(0,0,0,0) 55%, rgba(0,0,0,0.75) 100%)",
        }}
      />

      {/* Content */}
      <motion.div
        className="relative h-full flex flex-col justify-end px-6 sm:px-12 pb-20 sm:pb-28"
        style={{ y: contentY, opacity, rotateX, scale: contentScale, transformStyle: "preserve-3d" }}
      >
        <div className="max-w-[1320px] mx-auto w-full">
          <div className="text-[11px] uppercase tracking-[0.3em] mb-4" style={{ color: "#FF2800" }}>
            {v.year || (type === "moto" ? "Moto" : "Auto")}
          </div>
          <h2
            className="text-5xl sm:text-7xl mb-5 max-w-4xl"
            style={{ color: "#FFFFFF", fontWeight: 300, letterSpacing: "-0.03em", lineHeight: 1 }}
          >
            {v.title}
          </h2>
          {v.subtitle ? (
            <p className="text-[17px] max-w-[620px] mb-6" style={{ color: "rgba(255,255,255,0.85)", lineHeight: 1.5 }}>
              {v.subtitle}
            </p>
          ) : null}
          {price ? (
            <div className="text-3xl sm:text-4xl mb-7" style={{ color: "#FFFFFF", fontWeight: 400 }}>
              {price}
            </div>
          ) : v.sale_status === "accepting_offers" ? (
            <div className="text-[16px] mb-7" style={{ color: "rgba(255,255,255,0.7)" }}>Su richiesta</div>
          ) : null}

          <div className="flex flex-wrap items-center gap-3">
            <a
              href={waHref}
              target="_blank"
              rel="noreferrer"
              className="px-7 py-3.5 text-[14px]"
              style={{ background: "#25D366", color: "#FFFFFF", fontWeight: 500 }}
              data-testid={`vehicle-panel-wa-${v.slug}`}
            >
              WhatsApp
            </a>
            <Link
              to={`/veicolo/${v.slug}`}
              className="px-7 py-3.5 text-[14px]"
              style={{ background: "transparent", color: "#FFFFFF", border: "1px solid rgba(255,255,255,0.4)" }}
              data-testid={`vehicle-panel-scheda-${v.slug}`}
            >
              Scheda completa
            </Link>
          </div>
        </div>
      </motion.div>
    </section>
  );
}

function TypeSwitch({ type }) {
  return (
    <div className="inline-flex p-1" style={{ background: "rgba(0,0,0,0.06)" }} data-testid="marketplace-switch">
      {["auto", "moto"].map((t) => (
        <Link
          key={t}
          to={`/${t}`}
          className="px-5 py-2 text-[13px] uppercase tracking-[0.2em] transition-colors duration-300"
          style={{ background: type === t ? "#111" : "transparent", color: type === t ? "#FFF" : "#5a5a5a" }}
          data-testid={`marketplace-switch-${t}`}
        >
          {t}
        </Link>
      ))}
    </div>
  );
}

function OtherEmpty({ type }) {
  return (
    <div
      className="py-14 px-6 text-center"
      style={{ background: "#FFFFFF", border: "1px solid rgba(0,0,0,0.06)" }}
    >
      <div className="text-[11px] uppercase tracking-[0.3em] mb-3" style={{ color: "#8a8a8a" }}>
        Presto qui
      </div>
      <p className="text-[17px] max-w-[520px] mx-auto mb-6" style={{ color: "#2b2b2b", lineHeight: 1.55 }}>
        Le altre {type === "moto" ? "moto" : "auto"} in vendita compariranno qui, sotto il Veicolo del mese. Nel frattempo scrivimi per sapere cosa c'è in garage.
      </p>
      <a
        href="https://wa.me/393520463627"
        target="_blank"
        rel="noreferrer"
        className="inline-block px-7 py-3 text-[14px]"
        style={{ background: "#25D366", color: "#FFFFFF", fontWeight: 500 }}
        data-testid="other-empty-wa"
      >
        Scrivimi su WhatsApp
      </a>
    </div>
  );
}

/**
 * Big "veicolo del mese" hero on top of /auto and /moto. Uses `is_featured`.
 * Shows a large photo, price, description, WA + Scheda + optional inline
 * YouTube embed from `interview_youtube_url` or `hero_video_url`.
 */
function FeaturedHero({ v, type }) {
  const hero = v.hero_image || (v.gallery && v.gallery[0]) || "";
  const heroSrc = hero && hero.startsWith("/")
    ? `${process.env.REACT_APP_BACKEND_URL}${hero}`
    : hero;
  const price = v.price ? formatPrice(v.price) : "";
  const waHref = `${WHATSAPP_HREF}?text=${encodeURIComponent(
    `Ciao, mi interessa il ${type === "moto" ? "veicolo del mese" : "veicolo del mese"} ${v.title}`
  )}`;
  const ytId = extractYouTubeId(v.interview_youtube_url || v.hero_video_url);

  return (
    <section className="px-6 sm:px-12 pb-20" data-testid="vehicle-featured">
      <div className="max-w-[1320px] mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <span
            className="text-[10px] uppercase tracking-[0.3em] px-3 py-1"
            style={{ background: "#FF2800", color: "#FFFFFF", fontWeight: 500 }}
          >
            Veicolo del mese
          </span>
          <span className="text-[11px] uppercase tracking-[0.28em]" style={{ color: "#8a8a8a" }}>
            {v.year || ""}
          </span>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="grid lg:grid-cols-[1.4fr_1fr] gap-8"
        >
          {/* Media */}
          <Link to={`/veicolo/${v.slug}`} className="block" style={{ background: "#111", aspectRatio: "16/10" }}>
            {heroSrc ? (
              <img
                src={heroSrc}
                alt={v.title}
                loading="eager"
                decoding="async"
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full" style={{ background: "linear-gradient(135deg, #d8d3c9 0%, #b5aea3 100%)" }} />
            )}
          </Link>

          {/* Info + video */}
          <div className="flex flex-col justify-between">
            <div>
              <h2
                className="text-4xl sm:text-5xl mb-4"
                style={{ color: "#111", fontWeight: 300, letterSpacing: "-0.02em", lineHeight: 1.05 }}
              >
                {v.title}
              </h2>
              {v.subtitle ? (
                <p className="text-[16px] mb-4" style={{ color: "#5a5a5a", lineHeight: 1.5 }}>
                  {v.subtitle}
                </p>
              ) : null}
              {price ? (
                <div className="text-2xl mb-6" style={{ color: "#111", fontWeight: 500 }}>
                  {price}
                </div>
              ) : v.sale_status === "accepting_offers" ? (
                <div className="text-[16px] mb-6" style={{ color: "#8a8a8a" }}>Su richiesta</div>
              ) : null}

              <div className="flex flex-wrap items-center gap-3 mb-6">
                <a
                  href={waHref}
                  target="_blank"
                  rel="noreferrer"
                  className="px-6 py-3 text-[14px]"
                  style={{ background: "#25D366", color: "#FFFFFF", fontWeight: 500 }}
                  data-testid="featured-wa"
                >
                  WhatsApp
                </a>
                <Link
                  to={`/veicolo/${v.slug}`}
                  className="px-6 py-3 text-[14px]"
                  style={{ background: "#111", color: "#FFFFFF" }}
                  data-testid="featured-scheda"
                >
                  Scheda completa
                </Link>
              </div>
            </div>

            {/* Inline video */}
            {ytId ? (
              <div className="w-full" style={{ aspectRatio: "16/9" }}>
                <iframe
                  data-cookieconsent="ignore"
                  src={`https://www.youtube-nocookie.com/embed/${ytId}?rel=0`}
                  title={`Video ${v.title}`}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full"
                  data-testid="featured-video"
                />
              </div>
            ) : null}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function extractYouTubeId(url) {
  if (!url) return "";
  const m = String(url).match(
    /(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/\s]{11})/
  );
  return m ? m[1] : "";
}

function EmptyState({ type }) {
  return (
    <div className="py-24 text-center">
      <div className="text-[11px] uppercase tracking-[0.3em] mb-4" style={{ color: "#8a8a8a" }}>
        In arrivo
      </div>
      <h2 className="text-3xl sm:text-4xl mb-6" style={{ color: "#111", fontWeight: 300 }}>
        Nessun{type === "moto" ? "a moto" : "'auto"} in vendita adesso.
      </h2>
      <p className="text-[16px] mb-8 max-w-[520px] mx-auto" style={{ color: "#5a5a5a" }}>
        Il prossimo pezzo entra a breve. Iscriviti alla newsletter o scrivi su WhatsApp per essere avvisato prima.
      </p>
      <a
        href={WHATSAPP_HREF}
        target="_blank"
        rel="noreferrer"
        className="inline-block px-8 py-3.5 text-[14px]"
        style={{ background: "#FF2800", color: "#FFFFFF", fontWeight: 500 }}
      >
        Scrivi su WhatsApp
      </a>
    </div>
  );
}

function formatPrice(price) {
  if (!price) return "";
  // Se è già formattato con € tienilo così
  if (typeof price === "string" && /€|\$|£/.test(price)) return price;
  const n = Number(String(price).replace(/[^\d.,]/g, "").replace(",", "."));
  if (!Number.isFinite(n) || n <= 0) return String(price);
  return `€ ${n.toLocaleString("it-IT")}`;
}
